import React from 'react';
import Icon from '../../../components/AppIcon';

const HealthIndexPreview = () => {
  const metrics = [
    {
      id: 1,
      label: 'Business Health Index',
      value: 85,
      maxValue: 100,
      icon: 'Activity',
      color: 'success',
      description: 'Overall business performance score'
    },
    {
      id: 2,
      label: 'Strategy Strength',
      value: 78,
      maxValue: 100,
      icon: 'Target',
      color: 'primary',
      description: 'Business model effectiveness'
    },
    {
      id: 3,
      label: 'Financial Stability',
      value: 92,
      maxValue: 100,
      icon: 'TrendingUp',
      color: 'accent',
      description: 'Revenue and cash flow health'
    },
    {
      id: 4,
      label: 'Goal Feasibility',
      value: 71,
      maxValue: 100,
      icon: 'CheckCircle',
      color: 'secondary',
      description: 'Target achievement probability'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      success: 'bg-success text-success-foreground',
      primary: 'bg-primary text-primary-foreground',
      accent: 'bg-accent text-accent-foreground',
      secondary: 'bg-secondary text-secondary-foreground'
    };
    return colors?.[color] || colors?.primary;
  };

  const getProgressColor = (color) => {
    const colors = {
      success: 'bg-success',
      primary: 'bg-primary',
      accent: 'bg-accent',
      secondary: 'bg-secondary'
    };
    return colors?.[color] || colors?.primary;
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 border border-accent/20 rounded-full mb-4">
            <Icon name="Activity" size={16} color="var(--color-accent)" />
            <span className="text-xs md:text-sm font-medium text-accent font-caption">
              Dashboard Preview
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Track Your Business Health in Real-Time
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Get instant visibility into your business performance with AI-powered metrics and actionable insights.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {metrics?.map((metric) => (
            <div
              key={metric?.id}
              className="bg-card border border-border rounded-lg p-6 transition-smooth hover:elevation-md group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg ${getColorClasses(metric?.color)} flex items-center justify-center transition-smooth group-hover:scale-110`}>
                  <Icon
                    name={metric?.icon}
                    size={24}
                    color="currentColor"
                  />
                </div>
                <div className="text-right">
                  <p className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                    {metric?.value}
                  </p>
                  <p className="text-xs text-muted-foreground font-caption">
                    / {metric?.maxValue}
                  </p>
                </div>
              </div>

              <h3 className="text-sm md:text-base font-heading font-semibold text-foreground mb-2">
                {metric?.label}
              </h3>
              <p className="text-xs md:text-sm text-muted-foreground font-body mb-4">
                {metric?.description}
              </p>

              <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`absolute inset-y-0 left-0 ${getProgressColor(metric?.color)} rounded-full transition-smooth`}
                  style={{ width: `${(metric?.value / metric?.maxValue) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 md:mt-12 bg-gradient-to-r from-primary/5 via-secondary/5 to-accent/5 border border-border rounded-xl p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Icon name="Zap" size={32} color="var(--color-primary)" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-lg md:text-xl font-heading font-bold text-foreground mb-2">
                AI-Powered Insights & Recommendations
              </h3>
              <p className="text-sm md:text-base text-muted-foreground font-body">
                Get personalized recommendations to improve your scores and achieve your business goals faster with our intelligent analysis engine.
              </p>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-success/10 border border-success/20 rounded-full">
              <Icon name="TrendingUp" size={16} color="var(--color-success)" />
              <span className="text-sm font-medium text-success font-caption">
                +12% This Month
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HealthIndexPreview;